/**
 * Logger Service
 * Centralized logging with environment-aware output
 * Integrated with Axiom for analytics and monitoring
 */

import { featureFlags } from "@/services/features/feature-flags.service";
import { axiomService } from "@/services/axiom/axiom.service";

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  data?: unknown;
  context?: string;
}

class LoggerService {
  private isDevelopment = import.meta.env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  /**
   * Check if logging is enabled for a given level
   */
  private shouldLog(level: LogLevel): boolean {
    // Always log errors and warnings
    if (level === "error" || level === "warn") return true;

    // In production, only log if debug mode is enabled via feature flag
    if (!this.isDevelopment) {
      return featureFlags.isEnabled("ENABLE_DEBUG_MODE");
    }

    // In development, log everything
    return true;
  }

  /**
   * Format log message with context
   */
  private formatMessage(message: string, context?: string): string {
    if (context) {
      return `[${context}] ${message}`;
    }
    return message;
  }

  /**
   * Add log entry to history
   */
  private addToHistory(entry: LogEntry): void {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Debug level logging
   */
  debug(message: string, data?: unknown, context?: string): void {
    if (!this.shouldLog("debug")) return;

    const entry: LogEntry = {
      timestamp: new Date(),
      level: "debug",
      message: this.formatMessage(message, context),
      data,
      context,
    };

    this.addToHistory(entry);

    if (this.isDevelopment) {
      console.log(`🐛 ${entry.message}`, data || "");
    }

    // Send to Axiom
    axiomService.debug(entry.message, {
      ...data,
      context,
    });
  }

  /**
   * Info level logging
   */
  info(message: string, data?: unknown, context?: string): void {
    if (!this.shouldLog("info")) return;

    const entry: LogEntry = {
      timestamp: new Date(),
      level: "info",
      message: this.formatMessage(message, context),
      data,
      context,
    };

    this.addToHistory(entry);

    if (this.isDevelopment || featureFlags.isEnabled("ENABLE_DEBUG_MODE")) {
      console.info(`ℹ️ ${entry.message}`, data || "");
    }

    // Send to Axiom
    axiomService.info(entry.message, {
      ...data,
      context,
    });
  }

  /**
   * Warning level logging
   */
  warn(message: string, data?: unknown, context?: string): void {
    if (!this.shouldLog("warn")) return;

    const entry: LogEntry = {
      timestamp: new Date(),
      level: "warn",
      message: this.formatMessage(message, context),
      data,
      context,
    };

    this.addToHistory(entry);

    // Always show warnings in development
    if (this.isDevelopment) {
      console.warn(`⚠️ ${entry.message}`, data || "");
    }

    // Send to Axiom
    axiomService.warn(entry.message, {
      ...data,
      context,
    });
  }

  /**
   * Error level logging
   */
  error(message: string, error?: unknown, context?: string): void {
    const entry: LogEntry = {
      timestamp: new Date(),
      level: "error",
      message: this.formatMessage(message, context),
      data: error,
      context,
    };

    this.addToHistory(entry);

    // Always show errors in development
    if (this.isDevelopment) {
      console.error(`❌ ${entry.message}`, error || "");
    }

    // In production, send to error tracking service (Sentry)
    if (!this.isDevelopment && error instanceof Error) {
      // Sentry will be notified automatically
    }

    // Send to Axiom
    axiomService.error(entry.message, error, {
      context,
    });
  }

  /**
   * Get log history
   */
  getHistory(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter((log) => log.level === level);
    }
    return [...this.logs];
  }

  /**
   * Clear log history
   */
  clearHistory(): void {
    this.logs = [];
  }

  /**
   * Group related logs
   */
  group(label: string): void {
    if (this.isDevelopment && this.shouldLog("debug")) {
      console.group(label);
    }
  }

  /**
   * End log group
   */
  groupEnd(): void {
    if (this.isDevelopment && this.shouldLog("debug")) {
      console.groupEnd();
    }
  }

  /**
   * Measure performance
   */
  time(label: string): void {
    if (this.isDevelopment && this.shouldLog("debug")) {
      console.time(label);
    }
  }

  /**
   * End performance measurement
   */
  timeEnd(label: string): void {
    if (this.isDevelopment && this.shouldLog("debug")) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
export const logger = new LoggerService();

// Export a factory for creating context-specific loggers
export function createLogger(context: string) {
  return {
    debug: (message: string, data?: unknown) =>
      logger.debug(message, data, context),
    info: (message: string, data?: unknown) =>
      logger.info(message, data, context),
    warn: (message: string, data?: unknown) =>
      logger.warn(message, data, context),
    error: (message: string, error?: unknown) =>
      logger.error(message, error, context),
  };
}
